import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpBaseService, SnackbarMsgService } from 'app/shared/services';
import { environment } from 'environments/environment';
import { IClientApplicationWithUsers, IUserSimple } from './@shared/models';
import { EnterpriseLoadingEnum } from './@shared';
import { IUserDetail, IUserForCreation, ILinkUserRequest, IPermissionUserResponse, IPermissionEnterpriseUserData, IPermission } from './detail/models';

@Injectable({
    providedIn: 'root'
})
export class EnterpriseApiService extends HttpBaseService {
    constructor() {
        super(inject(HttpClient), inject(SnackbarMsgService));
    }

    getClientApplications(params: any = null, loadingType: EnterpriseLoadingEnum = EnterpriseLoadingEnum.Loading) {
        const queryParams = params ? new HttpParams({ fromObject: params }) : new HttpParams();

        return this._http.get<IClientApplicationWithUsers[]>(
            environment.apiUrl + 'enterprise/applications',
            {
                params: queryParams,
                observe: 'response'
            }
        ).pipe(
            this._handleError(false),
            this._mapPagination()
        );
    }

    getUsersForApplication(
        clientApplicationId: string,
        params: any = null,
        loadingType: EnterpriseLoadingEnum = EnterpriseLoadingEnum.LoadingUsers
    ) {
        const queryParams = params ? new HttpParams({ fromObject: params }) : new HttpParams();

        return this._http.get<IUserSimple[]>(
            environment.apiUrl + `enterprise/applications/${clientApplicationId}/users`,
            {
                params: queryParams,
                observe: 'response'
            }
        ).pipe(
            this._handleError(false),
            this._mapPagination()
        );
    }

    getUserDetail(params: { id?: string; email?: string; applicationId?: string }) {
        let httpParams = new HttpParams();
        
        if (params.id) {
            httpParams = httpParams.set('id', params.id);
        }
        if (params.email) {
            httpParams = httpParams.set('email', params.email);
        }
        if (params.applicationId) {
            httpParams = httpParams.set('applicationId', params.applicationId);
        }

        return this._http.get<IUserDetail>(
            `${environment.apiUrl}enterprise/users/detail`,
            { params: httpParams }
        ).pipe(
            this._handleError(true, true)
        );
    }

    createUser(userData: IUserForCreation) {
        return this._http.post(
            `${environment.apiUrl}enterprise/users`,
            userData
        ).pipe(
            this._handleError()
        );
    }

    updateUser(userId: string, userData: Partial<IUserForCreation>) {
        return this._http.put(
            `${environment.apiUrl}enterprise/users/${userId}`,
            userData
        ).pipe(
            this._handleError()
        );
    }

    resetUserPassword(userId: string) {
        return this._http.post(
            `${environment.apiUrl}enterprise/users/${userId}/reset-password`,
            {}
        ).pipe(
            this._handleError()
        );
    }

    linkUserToClient(linkData: ILinkUserRequest) {
        return this._http.post(
            `${environment.apiUrl}enterprise/users/applications/access`,
            linkData
        ).pipe(
            this._handleError(true, true)
        );
    }

    unlinkUserFromClient(userAccessId: string) {
        return this._http.delete<void>(
            `${environment.apiUrl}enterprise/users/applications/access/${userAccessId}`
        ).pipe(
            this._handleError(true, true)
        );
    }

    addUserPermission(data: IPermissionEnterpriseUserData) {
        return this._http.post<IPermissionUserResponse>(
            `${environment.apiUrl}enterprise/permissions-users`,
            data
        ).pipe(
            this._handleError()
        );
    }

    removeUserPermission(permissionUserId: string) {
        return this._http.delete<void>(
            `${environment.apiUrl}enterprise/permissions-users/${permissionUserId}`
        ).pipe(
            this._handleError()
        );
    }

    getUserPermissions(userId: string, applicationId: string, clientApplicationId: string) {
        return this._http.get<IPermissionUserResponse[]>(
            `${environment.apiUrl}enterprise/permissions-users?userId=${userId}&applicationId=${applicationId}&clientApplicationId=${clientApplicationId}`
        ).pipe(
            this._handleError()
        );
    }

    getPermissions(clientApplicationId: string) {
        return this._http.get<IPermission[]>(
            `${environment.apiUrl}enterprise/permissions?clientApplicationId=${clientApplicationId}`
        ).pipe(
            this._handleError()
        );
    }

    createClientAdmin(data: { name: string; email: string; clientApplicationId: string }) {
        return this._http.post<any>(
            `${environment.apiUrl}enterprise/client-admin`,
            { data }
        ).pipe(
            this._handleError()
        );
    }
}

